import { ALL_APPS as SHARED_ALL_APPS, type TAppConfig } from '@onesource/common-config';
import { CoerceBoolean, EAppNameEnum } from '@onesource/schemas';
import { object, string } from 'zod';

import { TFeatureFlagKey } from './hooks/use-feature-flag-utils';

const envSchema = object({
  VITE_BASE_API_ENDPOINT: string().url(),
  VITE_CDN_BASE_URL: string().url(),
  VITE_SHOW_DEV_TOOLS: CoerceBoolean,
  VITE_FIT_FRONTEND_URL: string().url(),
  VITE_RYC_FRONTEND_URL: string().url(),
  VITE_PROGRAMS_FE_URL: string().url(),
  VITE_WP_MEMBERS_SITE: string().url(),
  VITE_NOVU_APPLICATION_IDENTIFIER: string(),
  VITE_GA4_MEASUREMENT_ID: string().optional(),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  throw new Error(
    `Missing required environment variables:\n${parsed.error.issues.map(i => `  - ${i.path.join('.')}: ${i.message}`).join('\n')}`,
  );
}

const env = parsed.data;

export const SHOW_DEV_TOOLS = env.VITE_SHOW_DEV_TOOLS;

export const API_BASE_URL = env.VITE_BASE_API_ENDPOINT;
export const CDN_BASE_URL = env.VITE_CDN_BASE_URL;

// App URLs for SSO
export const FIT_FRONTEND_URL = env.VITE_FIT_FRONTEND_URL;
export const RYC_FRONTEND_URL = env.VITE_RYC_FRONTEND_URL;
export const PROGRAMS_FRONTEND_URL = env.VITE_PROGRAMS_FE_URL;

// A MINI-program app key is the sanitized (uppercased, `-`→`_`) form of its
// programs-fe slug, so lower-casing and swapping `_`→`-` recovers the slug
// (e.g. `ROOTED_RECOVERY` → `rooted-recovery`).
export const appKeyToSlug = (appKey: string): string => appKey.toLowerCase().replace(/_/g, '-');

// App keys are inconsistently cased / separated across the registry vs the space
// catalog (e.g. `MAPPING_THE_PELVIC_FLOOR` vs `mapping_the_pelvic_floor`). Match
// them on a normalized key so a grouped app resolves to its space regardless.
export const normalizeAppKey = (appKey: string): string => appKey.trim().replace(/-/g, '_').toUpperCase();

export const WP_MEMBERS_SITE = env.VITE_WP_MEMBERS_SITE;
export const NOVU_APPLICATION_IDENTIFIER = env.VITE_NOVU_APPLICATION_IDENTIFIER;

export const GA4_MEASUREMENT_ID = env.VITE_GA4_MEASUREMENT_ID;

// App configuration with subscription keys (re-exported, extended with members-fe-only fields)
export type { TAppConfig };

export interface TAppConfigWithFeatureFlag extends TAppConfig {
  featureFlagKey?: TFeatureFlagKey;
}

export const FIT_PRODUCT_ID = 21739;
export const FIT_PRODUCT_URL = 'https://members.laurenohayon.com/product/ryc-fit-yearly';
export const CHANGE_FIT_PLAN_URL = 'https://members.laurenohayon.com/fit-change-plan';

// App URL by id, env-dependent so it can't live in the shared config package.
const APP_URL_BY_ID: Partial<Record<TAppConfig['id'], string>> = {
  [EAppNameEnum.RYC]: RYC_FRONTEND_URL,
  [EAppNameEnum.FIT]: FIT_FRONTEND_URL,
};

// All available apps CONFIGURATION, sourced from @onesource/common-config with env URLs attached.
export const ALL_APPS: TAppConfigWithFeatureFlag[] = SHARED_ALL_APPS.map(app => ({
  ...app,
  url: APP_URL_BY_ID[app.id] ?? app.url,
}));

// Legacy export for backward compatibility - apps with URLs configured
export const AVAILABLE_APPS = ALL_APPS.filter(app => app.url);
export type TAvailableApp = typeof ALL_APPS;
